import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { CalendarDays, Download, Mail } from 'lucide-react';
import { apiClient } from '../../api/client';
import {
  useDeleteVisit,
  useEmailVisitIcs,
  useUpdateVisit,
  useUpdateVisitStatus,
  useVisit
} from './hooks';
import { kindLabel } from '../receipts/kindLabel';
import type { ReceiptKind } from '../receipts/types';
import { useRecordConfig } from '../customers/recordHooks';
import { VisitForm } from './VisitForm';
import { usePageTitle } from '../../hooks/usePageTitle';
import type { VisitStatus } from './types';

export function VisitDetailPage() {
  usePageTitle('Visit');
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data: visit, isLoading } = useVisit(id);
  const { data: recordConfig } = useRecordConfig();
  const update = useUpdateVisit(id);
  const updateStatus = useUpdateVisitStatus();
  const remove = useDeleteVisit();
  const emailIcs = useEmailVisitIcs(id);

  const [editing, setEditing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (isLoading) return <div className="max-w-3xl mx-auto px-4 py-8 text-slate-500">Loading…</div>;
  if (!visit)
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-slate-500">
        Visit not found.{' '}
        <Link to="/calendar" className="text-sky-600">Back to calendar</Link>
      </div>
    );

  const downloadIcs = async () => {
    const base = import.meta.env.VITE_API_BASE_URL;
    const res = await apiClient.get(`${base}/api/v1/visits/${id}/ics`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visit-${id.replaceAll('-', '')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/calendar" className="text-sm text-slate-500 hover:text-sky-600">← Calendar</Link>
          <h1 className="text-2xl font-semibold text-slate-800 mt-2">{visit.customerName}</h1>
          <p className="text-slate-500 text-sm mt-1">
            {new Date(visit.startUtc).toLocaleString()} — {new Date(visit.endUtc).toLocaleString()}
          </p>
        </div>
        <StatusBadge status={visit.status} />
      </div>

      {!editing ? (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
          {visit.locationAddress && (
            <p className="text-slate-700"><strong>Location: </strong>{visit.locationAddress}</p>
          )}
          {visit.mileageMiles !== null && (
            <p className="text-slate-700"><strong>Mileage: </strong>{visit.mileageMiles} miles</p>
          )}
          {visit.notes && (
            <p className="text-slate-700 whitespace-pre-wrap"><strong>Notes: </strong>{visit.notes}</p>
          )}
          {visit.records.length > 0 && (
            <p className="text-slate-600 text-sm">
              <strong className="text-slate-700">{recordConfig?.pluralLabel ?? 'Records'}: </strong>
              {visit.records.map((r) => r.primaryName).join(', ')}
            </p>
          )}
        </section>
      ) : (
        <section className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Edit visit</h2>
          <VisitForm
            lockCustomer
            defaultValues={{
              customerId: visit.customerId,
              startLocal: toLocalInput(new Date(visit.startUtc)),
              endLocal: toLocalInput(new Date(visit.endUtc)),
              locationAddress: visit.locationAddress ?? '',
              notes: visit.notes ?? '',
              mileageMiles: visit.mileageMiles?.toString() ?? ''
            }}
            submitLabel="Save"
            submitting={update.isPending}
            errorMessage={errorMsg}
            onSubmit={async (payload) => {
              setErrorMsg(null);
              try {
                await update.mutateAsync(payload);
                setEditing(false);
              } catch (err) {
                setErrorMsg(readConflictMessage(err) ?? 'Save failed.');
              }
            }}
            onCancel={() => setEditing(false)}
          />
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h2 className="text-sm font-semibold text-slate-700">Actions</h2>
        <div className="flex flex-wrap gap-2">
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 text-sm"
            >
              Edit
            </button>
          )}
          <button
            type="button"
            onClick={downloadIcs}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 text-sm inline-flex items-center gap-2"
          >
            <Download size={16} /> Download .ics
          </button>
          <button
            type="button"
            onClick={() => emailIcs.mutate()}
            disabled={emailIcs.isPending}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 disabled:opacity-50 text-sm inline-flex items-center gap-2"
          >
            <Mail size={16} /> {emailIcs.isPending ? 'Emailing…' : 'Email invite'}
          </button>

          {visit.status === 'Scheduled' && (
            <>
              <TransitionButton label="Mark completed" onClick={() => updateStatus.mutate({ id, status: 'Completed' })} />
              <TransitionButton label="Cancel visit" onClick={() => updateStatus.mutate({ id, status: 'Cancelled' })} />
            </>
          )}
          {visit.status === 'InProgress' && (
            <TransitionButton label="Mark completed" onClick={() => updateStatus.mutate({ id, status: 'Completed' })} />
          )}

          {visit.status === 'Completed' && (
            <Link
              to={`/visits/${id}/close`}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 text-sm inline-flex items-center gap-2"
            >
              <CalendarDays size={16} /> Close out (final bill)
            </Link>
          )}

          {visit.status === 'Scheduled' &&
            visit.receipts.length === 0 &&
            visit.expenses.length === 0 && (
              <button
                type="button"
                onClick={() => {
                  if (confirm('Delete this visit? This cannot be undone.')) {
                    remove.mutate(id, { onSuccess: () => nav('/calendar') });
                  }
                }}
                className="border border-red-300 text-red-700 px-4 py-2 rounded hover:bg-red-50 text-sm"
              >
                Delete
              </button>
            )}
        </div>
        {emailIcs.isSuccess && (
          <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
            Invite emailed — outbox id{' '}
            <span className="font-mono">{emailIcs.data?.outboxId.slice(0, 8)}</span>.
          </p>
        )}
      </section>

      {visit.expenses.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Expenses</h2>
          <ul className="text-sm space-y-1">
            {visit.expenses.map((e) => (
              <li key={e.id} className="flex justify-between">
                <span>
                  <strong>{e.category}: </strong>
                  {e.description}
                  {e.billed && <span className="ml-2 text-xs text-slate-500">(billed)</span>}
                </span>
                <span className="font-mono">{e.amount.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {visit.receipts.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Receipts</h2>
          <ul className="text-sm space-y-1">
            {visit.receipts.map((r) => (
              <li key={r.id} className="flex justify-between">
                <Link to={`/receipts/${r.id}`} className="text-sky-600 hover:text-sky-700 font-mono">
                  {r.receiptNumber}
                </Link>
                <span className="text-slate-600">
                  {kindLabel(r.kind as ReceiptKind)} • {r.status} • {r.total.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function TransitionButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 text-sm"
    >
      {label}
    </button>
  );
}

function StatusBadge({ status }: { status: VisitStatus }) {
  const palette: Record<string, string> = {
    Scheduled: 'bg-sky-50 text-sky-800 border-sky-200',
    InProgress: 'bg-amber-50 text-amber-800 border-amber-200',
    Completed: 'bg-green-50 text-green-800 border-green-200',
    Cancelled: 'bg-slate-100 text-slate-600 border-slate-200'
  };
  return (
    <span className={`inline-flex items-center text-xs px-3 py-1 rounded border ${palette[status] ?? ''}`}>
      {status}
    </span>
  );
}

function toLocalInput(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes())
  );
}

function readConflictMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as { message?: string } | undefined;
  return data?.message ?? null;
}
